import { Injector, ValueProvider } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { AND_OPERATOR, DYNAMIC_MATCHERS, DynamicFormControlCondition, DynamicFormControlMatcher, DynamicFormControlModel, DynamicFormControlRelation, DynamicFormRelationService, OR_OPERATOR } from '@ng-dynamic-forms/core';
import { arrayContentsEqual } from '../helpers/array-contents-equal.helper';
import { compareProps } from '../helpers/compare-props.helper';

export const MATCH_COMPARE_WITH_VISIBLE = 'COMPARE_WITH_VISIBLE';
export const MATCH_COMPARE_WITH_HIDDEN = 'COMPARE_WITH_HIDDEN';

type CompareWith = string | string[] | ((a, b?) => boolean);
type CompareWithCondition = DynamicFormControlCondition & { compareWith?: CompareWith };
type CompareWithRelation = DynamicFormControlRelation & { when: CompareWithCondition[] };

export const COMPARE_WITH_FNS: Record<string, (a, b) => boolean> = {
  // ...add compareWith functions here
} as const;

/**
 * This matcher re-implements some parts of the dynamic form relation service,
 * in order to allow assessing relationships based on custom compareWithFn functions.
 *
 * This matcher uses the compareWith property of the relation to determine matches instead of a
 * simple equals check, to enable more flexible matching of options.
 *
 * If the compareWith property is a function, it is used to compare values.
 * If the compareWith property is a string, it is used as the key of a predefined function from the COMPARE_WITH_FNS object defined above.
 * If the compareWith property is an array of strings, they are used as properties to compare using compareProps().
 *
 * Additionally, if the related model's value is an array, matching occurs as follows:
 * If condition.value is an array -> check that condition values exactly match model values (ignoring sequence).
 * If condition.value is not an array -> check that model values include condition value.
 */
export const CompareWithVisibleMatcher: DynamicFormControlMatcher = {
  match:         MATCH_COMPARE_WITH_VISIBLE,
  opposingMatch: MATCH_COMPARE_WITH_HIDDEN,
  onChange(hasMatch, model: DynamicFormControlModel, control: UntypedFormControl, injector: Injector) {
    const relationService: DynamicFormRelationService = injector.get(DynamicFormRelationService);
    const relatedFormControls: Record<string, UntypedFormControl> = relationService.getRelatedFormControls(model, control.root as UntypedFormGroup);
    const relation: CompareWithRelation = relationService.findRelationByMatcher(model.relations, CompareWithVisibleMatcher);
    const isMatch = relation && matchesCondition(relation, relatedFormControls, CompareWithVisibleMatcher);

    model.hidden = !isMatch;
  }
};

export const COMPARE_WITH_VISIBLE_MATCHER_PROVIDER: ValueProvider = {
  provide:  DYNAMIC_MATCHERS,
  useValue: CompareWithVisibleMatcher,
  multi:    true
};

// code copied/modified from ng-dynamic-forms, in order to use matches() function
function matchesCondition(
  relation: CompareWithRelation,
  relatedFormModels: Record<string, UntypedFormControl>,
  matcher: DynamicFormControlMatcher
): boolean {
  const operator = relation.operator || OR_OPERATOR;

  return relation.when.reduce((hasAlreadyMatched, condition, index) => {
    const path = condition.rootPath || condition.id;

    let relatedFormControl: UntypedFormControl;

    for (const [key, control] of Object.entries(relatedFormModels)) {
      if (key === path) {
        relatedFormControl = control;
        break;
      }
    }

    if (relatedFormControl && relation.match === matcher.match) {
      if (index > 0 && operator === AND_OPERATOR && !hasAlreadyMatched) {
        return false;
      }

      if (index > 0 && operator === OR_OPERATOR && hasAlreadyMatched) {
        return true;
      }

      return matches(relatedFormControl, condition);
    }

    if (relatedFormControl && relation.match === matcher.opposingMatch) {
      if (index > 0 && operator === AND_OPERATOR && hasAlreadyMatched) {
        return true;
      }

      if (index > 0 && operator === OR_OPERATOR && !hasAlreadyMatched) {
        return false;
      }

      return !matches(relatedFormControl, condition);
    }

    return false;
  }, false);
}

// this is the most important part of the code, that checks whether there is a match
function matches(control: UntypedFormControl, {
  compareWith,
  value
}: CompareWithCondition) {
  const matchFn =
    typeof compareWith === 'function'
      ? compareWith
      : typeof compareWith === 'string'
        ? COMPARE_WITH_FNS[compareWith]
        : Array.isArray(compareWith)
          ? (a, b) => compareProps(compareWith, a, b)
          : (a, b) => a === b;

  // if no value specified for comparison, assume matchFn is a unary function
  if (!value) {
    return matchFn(control.value);
  }

  if (Array.isArray(control.value)) {
    // if condition value is an array
    if (Array.isArray(value)) {
      // do an exact check on the contents of the model value
      return arrayContentsEqual(matchFn, control.value, value);
    }
    // otherwise check if model value includes condition value
    return control.value.some(val => matchFn(val, value));
  }

  // if value is not an array, do a normal comparison
  return matchFn(control.value, value);
}
